import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import AdminPagination from '../AdminPagination';
import LucideSymbolIcon from '../../icons/LucideSymbolIcon';
import { useAdminTablePagination, ADMIN_TABLE_PAGE_SIZE } from '../../../hooks/useAdminTablePagination';
import {
  expireAdminAcademiaWithdrawalPayout,
  fetchAdminAcademiaWalletWithdrawals,
  fetchAdminAcademiaWithdrawalConfirmationStatus,
  updateAdminAcademiaWalletWithdrawalStatus,
} from '../../../services/walletService';
import { formatMoney } from '../../../utils/currency';
import {
  pollPaymentStatus,
  WITHDRAWAL_PAYOUT_POLL_INTERVAL_MS,
  WITHDRAWAL_PAYOUT_POLL_MAX_ATTEMPTS,
} from '../../../utils/paymentPolling';
import type { WithdrawalRow, WithdrawalStatus } from '../../../types/admin';
import { AcademicPanelCard, AcademicPanelTableWrap } from './AcademicPanelCard';
import WithdrawalActionModal, { type WithdrawalModalPhase } from './WithdrawalActionModal';

type WithdrawalAction = 'APPROUVEE' | 'REJETEE' | 'PAYEE';

type ModalState = {
  row: WithdrawalRow;
  phase: WithdrawalModalPhase;
  action: WithdrawalAction;
};

type StatusFilter = Extract<WithdrawalStatus, 'EN_ATTENTE' | 'APPROUVEE' | 'EN_PAIEMENT' | 'ECHEC_PAIEMENT'>;

type Props = {
  onChanged?: () => void;
  showToast: (message: string, variant: 'success' | 'error') => void;
  status?: StatusFilter;
  sectionId?: string;
  title?: string;
  subtitle?: string;
  variant?: 'default' | 'accent' | 'preview' | 'success';
  emptyMessage?: string;
};

function formatShortDate(iso: string) {
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function roleLabel(role: string) {
  if (role === 'APPRENANT') return 'Apprenant';
  if (role === 'FORMATEUR') return 'Formateur';
  if (role === 'PARTENAIRE_INSTITUTIONNEL') return 'Partenaire';
  return role;
}

function canalLabel(canal?: string | null) {
  if (canal === 'MOBILE_MONEY') return 'Mobile Money';
  if (canal === 'BANQUE') return 'Banque';
  return canal ?? '—';
}

function normalizeWithdrawalErrorMessage(error: unknown, fallback: string) {
  const raw = error instanceof Error ? error.message.trim() : '';
  if (/compte marchand ne peut pas utiliser cet api/i.test(raw)) {
    return 'API retrait FlexPay non activée sur votre compte marchand. Contactez le support FlexPay (flexpay.cd) pour activer les payouts sortants sur JCONSULTMY.';
  }
  if (!raw || /^no message available$/i.test(raw)) {
    return fallback;
  }
  return raw;
}

export default function AcademiaPendingWithdrawalsSection({
  onChanged,
  showToast,
  status = 'EN_ATTENTE',
  sectionId,
  title,
  subtitle,
  variant = 'preview',
  emptyMessage,
}: Props) {
  const [rows, setRows] = useState<WithdrawalRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [modalState, setModalState] = useState<ModalState | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [pollAttempt, setPollAttempt] = useState(0);
  const pollAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      pollAbortRef.current?.abort();
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const result = await fetchAdminAcademiaWalletWithdrawals({
        status,
        page: 1,
        limit: 200,
      });
      setRows((result?.items ?? []) as WithdrawalRow[]);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Impossible de charger les retraits');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    void load();
  }, [load]);

  const {
    paginatedItems: pageRows,
    currentPage,
    totalPages,
    onPageChange,
    summary,
  } = useAdminTablePagination(rows, ADMIN_TABLE_PAGE_SIZE);

  const pendingTotal = useMemo(
    () => rows.reduce((sum, row) => sum + Number(row.montant || 0), 0),
    [rows],
  );
  const devise = rows[0]?.devise ?? 'CDF';

  const closeModal = useCallback(() => {
    if (isSubmitting || modalState?.phase === 'waiting-payout') {
      return;
    }
    setModalState(null);
    setIsSubmitting(false);
    setModalError(null);
    setPollAttempt(0);
  }, [isSubmitting, modalState?.phase]);

  const openConfirmModal = (row: WithdrawalRow, action: WithdrawalAction) => {
    setModalError(null);
    setModalState({
      row,
      action,
      phase:
        action === 'PAYEE'
          ? 'confirm-pay'
          : action === 'APPROUVEE'
            ? 'confirm-approve'
            : 'confirm-reject',
    });
  };

  const waitForPayoutConfirmation = useCallback(
    async (row: WithdrawalRow) => {
      pollAbortRef.current?.abort();
      const abort = new AbortController();
      pollAbortRef.current = abort;
      setModalState((prev) =>
        prev ? { ...prev, row, phase: 'waiting-payout', action: 'APPROUVEE' } : null,
      );
      setPollAttempt(0);
      setIsSubmitting(false);

      try {
        const finalStatus = await pollPaymentStatus({
          fetchStatus: async () => {
            const data = await fetchAdminAcademiaWithdrawalConfirmationStatus(row.id);
            return String(data?.statut ?? '').trim().toUpperCase();
          },
          onAttempt: (attempt) => setPollAttempt(attempt),
          signal: abort.signal,
          successStatuses: ['PAYEE'],
          failureStatuses: ['ECHEC_PAIEMENT', 'REJETEE'],
          maxAttempts: WITHDRAWAL_PAYOUT_POLL_MAX_ATTEMPTS,
          intervalMs: WITHDRAWAL_PAYOUT_POLL_INTERVAL_MS,
        });

        if (abort.signal.aborted) {
          return;
        }

        if (finalStatus === 'PAYEE') {
          showToast(`Payout confirmé — retrait ${row.reference} payé.`, 'success');
        } else if (finalStatus === 'ECHEC_PAIEMENT' || finalStatus === 'REJETEE') {
          showToast(`Payout refusé ou échoué pour ${row.reference}.`, 'error');
        } else if (finalStatus === 'TIMEOUT') {
          const timeoutRes = await expireAdminAcademiaWithdrawalPayout(row.id);
          showToast(
            typeof timeoutRes?.message === 'string' && timeoutRes.message.trim()
              ? timeoutRes.message
              : `Délai dépassé (45 s) — payout ${row.reference} en échec. Relancez l’approbation pour réessayer.`,
            'error',
          );
        }
      } catch (e) {
        const message = normalizeWithdrawalErrorMessage(
          e,
          'Erreur lors de l’attente de confirmation FlexPay.',
        );
        showToast(message, 'error');
      } finally {
        pollAbortRef.current = null;
        setModalState(null);
        setPollAttempt(0);
        setUpdatingId(null);
        await load();
        onChanged?.();
      }
    },
    [load, onChanged, showToast],
  );

  const executeModalAction = async (row: WithdrawalRow, action: WithdrawalAction) => {
    const isRetry = status === 'ECHEC_PAIEMENT' && action === 'APPROUVEE';
    const verb =
      action === 'PAYEE'
        ? 'marquer comme payé'
        : action === 'APPROUVEE'
          ? isRetry
            ? 'relancer'
            : 'approuver'
          : 'rejeter';

    setUpdatingId(row.id);
    setIsSubmitting(true);
    setModalError(null);
    let startedPayoutWait = false;
    try {
      const res = await updateAdminAcademiaWalletWithdrawalStatus(row.id, action);
      const awaitingConfirmation = Boolean(res?.awaitingConfirmation);
      const resultStatut = String(res?.data?.statut ?? '').trim().toUpperCase();

      if (action === 'APPROUVEE' && awaitingConfirmation) {
        startedPayoutWait = true;
        await waitForPayoutConfirmation(row);
        return;
      }

      showToast(
        typeof res?.message === 'string' && res.message.trim()
          ? res.message
          : action === 'PAYEE'
            ? `Retrait ${row.reference} payé — portefeuille débité.`
            : action === 'APPROUVEE'
              ? resultStatut === 'PAYEE'
                ? `Retrait ${row.reference} payé.`
                : `Demande ${row.reference} approuvée.`
              : `Demande ${row.reference} rejetée.`,
        resultStatut === 'ECHEC_PAIEMENT' ? 'error' : 'success',
      );
      setModalState(null);
      await load();
      onChanged?.();
    } catch (e) {
      const message = normalizeWithdrawalErrorMessage(
        e,
        action === 'APPROUVEE'
          ? 'Le payout FlexPay a échoué. Vérifiez FLEXPAY_BEARER_TOKEN, le numéro Mobile Money et API_URL.'
          : `Impossible de ${verb} la demande.`,
      );
      setModalError(message);
      showToast(message, 'error');
    } finally {
      setIsSubmitting(false);
      if (!startedPayoutWait) {
        setUpdatingId(null);
      }
    }
  };

  const resolvedTitle =
    title ??
    (status === 'APPROUVEE'
      ? 'Retraits approuvés (paiement manuel)'
      : status === 'EN_PAIEMENT'
        ? 'Payouts en cours'
        : status === 'ECHEC_PAIEMENT'
          ? 'Payouts en échec'
          : 'Retraits en attente');

  const resolvedSubtitle =
    subtitle ??
    (status === 'APPROUVEE'
      ? 'Effectuez le virement Mobile Money hors ligne, puis marquez la demande comme payée pour débiter le portefeuille.'
      : status === 'EN_PAIEMENT'
        ? 'Demandes envoyées à FlexPay — finalisation automatique par callback ou réconciliation.'
        : status === 'ECHEC_PAIEMENT'
          ? 'Échec provider — relancez le payout ou rejetez la demande.'
          : 'Demandes apprenants, formateurs et partenaires — vérifiez le solde et les coordonnées avant validation.');

  const resolvedEmpty =
    emptyMessage ??
    (status === 'APPROUVEE'
      ? 'Aucun retrait approuvé en attente de paiement manuel.'
      : status === 'EN_PAIEMENT'
        ? 'Aucun payout en cours de traitement.'
        : status === 'ECHEC_PAIEMENT'
          ? 'Aucun payout en échec.'
          : 'Aucune demande de retrait en attente pour le moment.');

  return (
    <>
    <AcademicPanelCard
      id={sectionId}
      icon={status === 'ECHEC_PAIEMENT' ? 'warning' : status === 'EN_PAIEMENT' ? 'hourglass_empty' : 'account_balance_wallet'}
      title={resolvedTitle}
      subtitle={resolvedSubtitle}
      variant={variant}
      flush
      badge={
        <span className={status === 'ECHEC_PAIEMENT' ? 'academic-panel-badge' : 'academic-panel-badge'}>
          {loading ? '…' : `${rows.length} dossier(s)`}
        </span>
      }
    >
      {!loading && rows.length > 0 && status === 'EN_ATTENTE' ? (
        <p className="academic-panel-stat-banner academic-panel-stat-banner--info mx-4 mt-4">
          <span className="inline-flex items-center gap-2">
            <LucideSymbolIcon name="payments" tone="inherit" size={16} />
            Montant cumulé en attente :{' '}
            <strong className="tabular-nums">{formatMoney(pendingTotal, devise)}</strong>
          </span>
        </p>
      ) : null}

      {loadError ? (
        <p className="mx-4 mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {loadError}
        </p>
      ) : null}

      {loading ? (
        <p className="academic-panel-empty">Chargement des demandes…</p>
      ) : rows.length === 0 ? (
        <p className="academic-panel-empty">{resolvedEmpty}</p>
      ) : (
        <>
          <AcademicPanelTableWrap>
            <table className="academic-panel-table">
              <thead>
                <tr>
                  <th>Demandeur</th>
                  <th>Rôle</th>
                  <th className="text-right">Montant</th>
                  <th>Canal / compte</th>
                  <th>Date</th>
                  {status !== 'EN_PAIEMENT' ? <th className="text-right">Actions</th> : null}
                </tr>
              </thead>
              <tbody>
                {pageRows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <p className="font-semibold text-slate-900">{row.utilisateur.name}</p>
                      <p className="mt-0.5 text-xs text-on-surface-variant">
                        Réf. {row.reference} · {row.utilisateur.telephone}
                      </p>
                      {status === 'EN_ATTENTE' ? (
                        <p className="text-xs text-on-surface-variant">
                          Solde {formatMoney(row.utilisateur.walletBalance, row.utilisateur.walletCurrency)}
                        </p>
                      ) : null}
                    </td>
                    <td>
                      <span className="academic-panel-badge academic-panel-badge--muted">
                        {roleLabel(row.utilisateur.role)}
                      </span>
                    </td>
                    <td className="text-right font-black text-primary tabular-nums">
                      {formatMoney(row.montant, row.devise)}
                    </td>
                    <td className="text-xs">
                      <p className="font-medium text-slate-800">{canalLabel(row.canalPayout)}</p>
                      <p className="text-on-surface-variant">{row.compteDestination ?? '—'}</p>
                    </td>
                    <td className="whitespace-nowrap text-xs text-on-surface-variant">
                      {formatShortDate(row.dateCreation)}
                    </td>
                    {status !== 'EN_PAIEMENT' ? (
                      <td>
                        <div className="flex flex-wrap justify-end gap-2">
                          {status === 'APPROUVEE' ? (
                            <button
                              type="button"
                              disabled={updatingId === row.id || Boolean(modalState)}
                              className="academic-panel-btn academic-panel-btn--primary"
                              onClick={() => openConfirmModal(row, 'PAYEE')}
                            >
                              <LucideSymbolIcon name="payments" tone="inherit" size={14} />
                              Marquer payé
                            </button>
                          ) : null}
                          {status === 'EN_ATTENTE' || status === 'ECHEC_PAIEMENT' ? (
                            <button
                              type="button"
                              disabled={updatingId === row.id || Boolean(modalState)}
                              className="academic-panel-btn academic-panel-btn--primary"
                              onClick={() => openConfirmModal(row, 'APPROUVEE')}
                            >
                              <LucideSymbolIcon
                                name={status === 'ECHEC_PAIEMENT' ? 'refresh' : 'check_circle'}
                                tone="inherit"
                                size={14}
                              />
                              {status === 'ECHEC_PAIEMENT' ? 'Relancer' : 'Approuver'}
                            </button>
                          ) : null}
                          {status === 'EN_ATTENTE' || status === 'ECHEC_PAIEMENT' ? (
                            <button
                              type="button"
                              disabled={updatingId === row.id || Boolean(modalState)}
                              className="academic-panel-btn academic-panel-btn--danger"
                              onClick={() => openConfirmModal(row, 'REJETEE')}
                            >
                              <LucideSymbolIcon name="cancel" tone="inherit" size={14} />
                              Rejeter
                            </button>
                          ) : null}
                        </div>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </AcademicPanelTableWrap>
          {rows.length > ADMIN_TABLE_PAGE_SIZE ? (
            <AdminPagination
              className="border-t border-outline-variant/15 px-4 py-3"
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={onPageChange}
              summary={summary}
            />
          ) : null}
        </>
      )}
    </AcademicPanelCard>

    <WithdrawalActionModal
      open={Boolean(modalState)}
      phase={modalState?.phase ?? 'confirm-approve'}
      row={modalState?.row ?? null}
      isRetry={status === 'ECHEC_PAIEMENT' && modalState?.action === 'APPROUVEE'}
      isSubmitting={isSubmitting}
      pollAttempt={pollAttempt}
      errorMessage={modalError}
      onConfirm={() => {
        if (!modalState) {
          return;
        }
        void executeModalAction(modalState.row, modalState.action);
      }}
      onCancel={closeModal}
    />
    </>
  );
}
